// eslint-disable-next-line no-restricted-imports
import {useEffect, useState} from 'react';
// eslint-disable-next-line no-restricted-imports
import {Text, Box, Flex, Image, Icon, useToast} from '@chakra-ui/react';
import {IconChevronRight} from '@tabler/icons';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import {
  Listing,
  useUserQuery,
  useGetActiveListingsQuery,
} from '../../graphql/generated';
import {useGeolocation} from '../../hooks/geolocation';
import {useTransactionDrawerContext} from '../../context/transaction-drawer';
import {InfoAlert} from '../global/info-alert';

interface MarketplaceListing {
  image?: string;
  marketplaceImage?: string;
  assetId: string;
}

const columnImage = {base: '0 66px', md: '0 120px'};
const columnQty = {base: '0 30px', md: '0 110px'};
const columnLocation = 1;
const columnPrice = {base: '0 90px', md: '0 150px'};
const columnIcon = {base: '0 40px', md: '0 60px'};

export const MarketplaceListing: React.FC<MarketplaceListing> = ({
  image,
  assetId,
}) => {
  const toast = useToast();
  const userQuery = useUserQuery();
  const {canBuyOnTheMarketplace, isLoadingGeolocation, countryCode} =
    useGeolocation();

  const {refetch, data, loading} = useGetActiveListingsQuery({
    variables: {assetId},
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    refetchWritePolicy: 'overwrite',
  });

  const {openDrawer, isActive, isLoading} = useTransactionDrawerContext();
  const [activeListings, setActiveListings] = useState<Listing[]>([]);
  const [userId, setUserId] = useState<string>('');

  const marketplaceDisabled = !isLoadingGeolocation && !canBuyOnTheMarketplace;

  useEffect(() => {
    if (!isActive || isLoading) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isLoading]);

  useEffect(() => {
    if (!loading && data?.getActiveListings) {
      setActiveListings(data.getActiveListings as Listing[]);
    }
    if (userQuery.data?.user.id) {
      setUserId(userQuery.data?.user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userQuery.data?.user.id, loading]);

  return (
    <Box
      w="100%"
      pb={{base: '60px', md: '80px'}}
      pt={{base: '24px', md: '32px'}}
      px="20px"
    >
      <Box maxW="920px" m="0 auto">
        <Box
          borderBottom="1px solid"
          borderColor="buttonGrey"
          id="marketplace-listings"
        >
          <Text
            textStyle="h6"
            display="inline-block"
            borderBottom="4px solid"
            color="currentcolor"
          >
            Marketplace Listings
          </Text>
        </Box>
        {activeListings.length > 0 && (
          <Box pt="32px">
            <Flex
              fontStyle="showpiece_h7"
              fontSize="12px"
              color="tone3"
              fontWeight={600}
              mb="8px"
            >
              <Box flex={columnImage} />
              <Box flex={columnQty}>QTY</Box>
              <Box flex={columnLocation}>Seller Location</Box>
              <Box flex={columnPrice}>Price Per Piece</Box>
              <Box flex={columnIcon} />
            </Flex>
            <Box>
              {activeListings.map((listing) => (
                <Flex
                  key={listing.id}
                  mb="8px"
                  borderRadius="24px"
                  background="background"
                  border="1px solid"
                  borderColor="gray.1000"
                  pt="12px"
                  pb="14px"
                  alignItems="center"
                  as="button"
                  width="100%"
                  textAlign="left"
                  transition="background .35s ease"
                  _hover={{
                    background: 'white',
                  }}
                  onClick={() => {
                    if (marketplaceDisabled && userId !== listing.userId) {
                      toast.closeAll();
                      toast({
                        position: 'bottom',
                        isClosable: true,
                        render: () => (
                          <InfoAlert
                            title={
                              countryCode === 'US'
                                ? 'Sorry, Collectors based in the US are not currently able to purchase on the Showpiece platform'
                                : 'Before you buy! Please be aware of the following'
                            }
                            description={
                              countryCode === 'US'
                                ? 'If you have any questions, please contact info@showpiece.com.'
                                : 'Our international payments provider doesn’t currently operate in your region. This means you will not be able to sell pieces to other users on the Showpiece marketplace. You will still have ownership of your showpiece, though, and can share or gift from your collection.  If you have any questions, please contact info@showpiece.com.'
                            }
                            bgColor="aqua.500"
                          />
                        ),
                      });
                    } else if (openDrawer) {
                      openDrawer('purchase', listing.assetId || '', listing.id);
                    }
                  }}
                >
                  <Box flex={columnImage} pl={{base: '16px', md: '32px'}}>
                    <Image
                      src={image}
                      width="38px"
                      height="38px"
                      alt={assetId}
                    />
                  </Box>
                  <Box flex={columnQty}>
                    <Text textStyle={{base: 'body4', md: 'body1'}}>
                      {listing.quantityAvailable}
                    </Text>
                  </Box>
                  <Box flex={columnLocation}>
                    <Text textStyle={{base: 'body4', md: 'body1'}}>
                      {`${
                        listing.sellerLocation
                          ? getUnicodeFlagIcon(listing.sellerLocation)
                          : ''
                      } ${listing.sellerLocation}`}
                    </Text>
                  </Box>
                  <Box flex={columnPrice}>
                    <Text textStyle={{base: 'h7', md: 'h6'}} color="aqua.500">
                      {listing.price.symbol}
                      {listing.price.value.toFixed(2)} {listing.price.postFix}
                    </Text>
                  </Box>
                  <Box flex={columnIcon}>
                    <Icon
                      as={IconChevronRight}
                      width="40px"
                      height="40px"
                      color="tone4"
                    />
                  </Box>
                </Flex>
              ))}
            </Box>
          </Box>
        )}
        {!activeListings.length && (
          <Text
            textAlign="center"
            borderRadius="24px"
            background="background"
            border="1px solid"
            borderColor="gray.1000"
            textStyle={{base: 'body4', md: 'body1'}}
            pt="16px"
            pb="18px"
            mt={{base: '24px', md: '32px'}}
          >
            There are no pieces currently available for this Showpiece on the
            marketplace!
          </Text>
        )}
      </Box>
    </Box>
  );
};
